/**
 * SLD generation for GeoServer WMS symbology.
 *
 * WMS tiles are rendered by the server, so the client cannot restyle them the
 * way it restyles GeoJSON vectors. GeoServer does accept an inline style
 * document via the `SLD_BODY` GetMap parameter, which is the supported way to
 * drive WMS symbology from an application — that is what this module builds.
 *
 *   LayerRenderer + geometry type → SLD 1.0 XML → SLD_BODY → GeoServer
 *
 * Limits worth knowing:
 *  - `SLD_BODY` travels in the tile URL, so the document must stay small.
 *    Servers/proxies commonly cap URLs around 8 KB.
 *  - Point symbols are limited to SLD `WellKnownName` marks; arbitrary SVG
 *    artwork is not portable, so the gallery is filtered for WMS layers.
 */

import type { LayerGeometryType, LayerRenderer } from '../types';
import { getLineDashArray, mergeCategoryStyle } from './symbols';
import {
  labelingIsActive,
  sldFontFor,
  type LabelClass,
  type LabelFilter,
  type LabelTextStyle,
  type LayerLabeling,
} from '../labeling/LabelTypes';

/** Escapes text placed into XML nodes/attributes. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function cssParam(name: string, value: string | number): string {
  return `<CssParameter name="${name}">${escapeXml(String(value))}</CssParameter>`;
}

/** SLD dash arrays are space-separated, unlike SVG's comma form. */
function sldDashArray(style: LayerRenderer['lineStyle'] | LayerRenderer['outlineStyle']): string {
  const dash = getLineDashArray(style as any);
  return dash ? cssParam('stroke-dasharray', dash.replace(/,/g, ' ')) : '';
}

function strokeBlock(color: string, width: number, opacity: number, dash: string): string {
  if (width <= 0) return '';
  return `<Stroke>${cssParam('stroke', color)}${cssParam('stroke-width', width)}${cssParam(
    'stroke-opacity',
    opacity,
  )}${dash}</Stroke>`;
}

/** Builds the symbolizer for one colour under the active geometry type. */
function buildSymbolizer(
  renderer: LayerRenderer,
  geometry: LayerGeometryType,
  color: string,
): string {
  const outlineDash = sldDashArray(renderer.outlineStyle);

  if (geometry === 'line') {
    return `<LineSymbolizer>${strokeBlock(
      color,
      renderer.outlineWidth || 1,
      renderer.fillOpacity,
      sldDashArray(renderer.lineStyle),
    )}</LineSymbolizer>`;
  }

  if (geometry === 'polygon') {
    const fill =
      renderer.fillStyle === 'transparent'
        ? ''
        : `<Fill>${cssParam('fill', color)}${cssParam('fill-opacity', renderer.fillOpacity)}</Fill>`;
    return `<PolygonSymbolizer>${fill}${strokeBlock(
      renderer.outlineColor,
      renderer.outlineWidth,
      1,
      outlineDash,
    )}</PolygonSymbolizer>`;
  }

  // Point (and unknown, which we treat as point).
  const mark = renderer.sldMark || 'circle';
  const stroke = strokeBlock(renderer.outlineColor, renderer.outlineWidth, 1, '');
  return `<PointSymbolizer><Graphic><Mark><WellKnownName>${escapeXml(
    mark,
  )}</WellKnownName><Fill>${cssParam('fill', color)}${cssParam(
    'fill-opacity',
    renderer.fillOpacity,
  )}</Fill>${stroke}</Mark><Size>${renderer.pointSize || 10}</Size><Rotation>${
    renderer.rotation ?? 0
  }</Rotation></Graphic></PointSymbolizer>`;
}

/* ─────────────────────────── Label symbolizer ───────────────────────────── */

/** `{a} mm` → `<ogc:PropertyName>a</ogc:PropertyName> mm`, for SLD's Label. */
function sldLabelContent(labelClass: LabelClass): string {
  const expression = labelClass.expression.trim();
  if (!expression) {
    const field = labelClass.field.trim();
    return field ? `<ogc:PropertyName>${escapeXml(field)}</ogc:PropertyName>` : '';
  }

  let out = '';
  let cursor = 0;
  for (const match of expression.matchAll(/\{([A-Za-z0-9_.:-]+)\}/g)) {
    const index = match.index ?? 0;
    if (index > cursor) out += escapeXml(expression.slice(cursor, index));
    out += `<ogc:PropertyName>${escapeXml(match[1])}</ogc:PropertyName>`;
    cursor = index + match[0].length;
  }
  out += escapeXml(expression.slice(cursor));
  return out;
}

/** Translates the structured label filter into an `ogc:Filter`. */
function sldLabelFilter(filter: LabelFilter): string {
  if (!filter.enabled || !filter.field.trim()) return '';
  const property = `<ogc:PropertyName>${escapeXml(filter.field.trim())}</ogc:PropertyName>`;
  const literal = `<ogc:Literal>${escapeXml(filter.value.trim())}</ogc:Literal>`;

  switch (filter.operator) {
    case '!=':
      return `<ogc:Filter><ogc:PropertyIsNotEqualTo>${property}${literal}</ogc:PropertyIsNotEqualTo></ogc:Filter>`;
    case '>':
      return `<ogc:Filter><ogc:PropertyIsGreaterThan>${property}${literal}</ogc:PropertyIsGreaterThan></ogc:Filter>`;
    case '<':
      return `<ogc:Filter><ogc:PropertyIsLessThan>${property}${literal}</ogc:PropertyIsLessThan></ogc:Filter>`;
    case 'contains':
      return `<ogc:Filter><ogc:PropertyIsLike wildCard="*" singleChar="." escapeChar="!">${property}<ogc:Literal>*${escapeXml(
        filter.value.trim(),
      )}*</ogc:Literal></ogc:PropertyIsLike></ogc:Filter>`;
    case '=':
    default:
      return `<ogc:Filter><ogc:PropertyIsEqualTo>${property}${literal}</ogc:PropertyIsEqualTo></ogc:Filter>`;
  }
}

/** SLD font weights are only normal/bold, so the finer UI weights round up. */
function sldFontWeight(weight: LabelTextStyle['fontWeight']): string {
  return weight === 'normal' ? 'normal' : 'bold';
}

/**
 * A Rule containing only the TextSymbolizer, so that a scale range on the
 * labels does not also hide the geometry.
 *
 * NOTE the deliberate swap: SLD's `MinScaleDenominator` is the ZOOMED-IN bound
 * (the rule applies when the denominator is at least this large), which is the
 * opposite of Esri's `minScale`. Getting this backwards makes labels appear
 * exactly where the user asked them to disappear.
 */
function buildLabelRule(
  labeling: LayerLabeling | undefined,
  geometry: LayerGeometryType,
): string {
  if (!labeling || !labelingIsActive(labeling)) return '';

  return labeling.classes
    .map((labelClass) => {
      const content = sldLabelContent(labelClass);
      if (!content) return '';

      const { text, halo, placement, scale } = labelClass;

      const font =
        `<Font>${cssParam('font-family', sldFontFor(text.fontFamily))}` +
        `${cssParam('font-size', text.fontSize)}` +
        `${cssParam('font-style', text.fontStyle)}` +
        `${cssParam('font-weight', sldFontWeight(text.fontWeight))}</Font>`;

      // Lines take a perpendicular offset; everything else takes an anchor
      // point plus displacement, which is how SLD expresses above/below/left.
      const isLine = geometry === 'line';
      const anchor = { center: [0.5, 0.5], above: [0.5, 0], below: [0.5, 1], left: [1, 0.5], right: [0, 0.5] } as Record<
        string,
        number[]
      >;
      const [anchorX, anchorY] = anchor[placement.type] ?? [0.5, 0.5];

      const labelPlacement = isLine
        ? `<LabelPlacement><LinePlacement><PerpendicularOffset>${
            placement.type === 'above-line'
              ? text.fontSize
              : placement.type === 'below-line'
                ? -text.fontSize
                : 0
          }</PerpendicularOffset></LinePlacement></LabelPlacement>`
        : `<LabelPlacement><PointPlacement>` +
          `<AnchorPoint><AnchorPointX>${anchorX}</AnchorPointX><AnchorPointY>${anchorY}</AnchorPointY></AnchorPoint>` +
          `<Displacement><DisplacementX>${placement.offsetX}</DisplacementX><DisplacementY>${-placement.offsetY}</DisplacementY></Displacement>` +
          `</PointPlacement></LabelPlacement>`;

      const haloBlock =
        halo.enabled && halo.width > 0
          ? `<Halo><Radius>${halo.width}</Radius><Fill>${cssParam('fill', halo.color)}</Fill></Halo>`
          : '';

      // GeoServer's own label engine handles collision and line-following, so
      // there is nothing to reimplement client-side for this path.
      const vendor =
        `<VendorOption name="conflictResolution">${labelClass.avoidOverlap}</VendorOption>` +
        (isLine && placement.parallel ? `<VendorOption name="followLine">true</VendorOption>` : '') +
        (isLine && placement.repeat > 0
          ? `<VendorOption name="repeat">${Math.round(placement.repeat)}</VendorOption>`
          : '');

      const scaleBlock = scale.enabled
        ? `${scale.maxScale > 0 ? `<MinScaleDenominator>${scale.maxScale}</MinScaleDenominator>` : ''}` +
          `${scale.minScale > 0 ? `<MaxScaleDenominator>${scale.minScale}</MaxScaleDenominator>` : ''}`
        : '';

      return (
        `<Rule><Name>${escapeXml(labelClass.id)}-label</Name>` +
        `${sldLabelFilter(labelClass.filter)}${scaleBlock}` +
        `<TextSymbolizer><Label>${content}</Label>${font}${labelPlacement}${haloBlock}` +
        `<Fill>${cssParam('fill', text.color)}</Fill>${vendor}</TextSymbolizer></Rule>`
      );
    })
    .join('');
}

/**
 * Labels-only SLD, sent as a SEPARATE transparent WMS layer.
 *
 * GeoServer applies exactly one style document per GetMap request, so folding
 * labels into the symbology document would mean synthesising a geometry style
 * for layers that have none — silently replacing the server's own cartography
 * the moment someone ticks "Enable labels". A second request carrying only the
 * TextSymbolizer leaves the existing tile request completely untouched and
 * simply draws text over it.
 *
 * Returns '' when there is nothing to label.
 */
export function buildLabelSld(
  layerName: string,
  labeling: LayerLabeling | undefined,
  geometry: LayerGeometryType,
): string {
  const rules = buildLabelRule(labeling, geometry);
  if (!rules) return '';

  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<StyledLayerDescriptor version="1.0.0"` +
    ` xmlns="http://www.opengis.net/sld"` +
    ` xmlns:ogc="http://www.opengis.net/ogc"` +
    ` xmlns:xlink="http://www.w3.org/1999/xlink">` +
    `<NamedLayer><Name>${escapeXml(layerName)}</Name>` +
    `<UserStyle><FeatureTypeStyle>${rules}</FeatureTypeStyle></UserStyle>` +
    `</NamedLayer></StyledLayerDescriptor>`
  );
}

/**
 * Produces a complete SLD document for a layer.
 * Returns an empty string when there is nothing to override.
 */
export function buildSld(
  layerName: string,
  renderer: LayerRenderer | undefined,
  geometry: LayerGeometryType,
): string {
  if (!renderer) return '';

  let rules = '';

  if (renderer.type === 'unique' && renderer.field && renderer.categories?.length) {
    // One rule per class, then a catch-all so unmatched features still draw.
    rules = renderer.categories
      .map((category) => {
        // Per-category symbol overrides (see RendererCategory) merged on top
        // of the renderer's global style, so GeoServer draws the same
        // per-class symbol this app's own map does.
        const effective = mergeCategoryStyle(renderer, category);
        // strTrim keeps rows whose stored value carries stray whitespace in the
        // same class as the trimmed value the UI classified on.
        return `<Rule><Name>${escapeXml(category.value)}</Name><Title>${escapeXml(
          category.label ?? category.value,
        )}</Title><ogc:Filter><ogc:PropertyIsEqualTo><ogc:Function name="strTrim"><ogc:PropertyName>${escapeXml(
          renderer.field as string,
        )}</ogc:PropertyName></ogc:Function><ogc:Literal>${escapeXml(
          category.value,
        )}</ogc:Literal></ogc:PropertyIsEqualTo></ogc:Filter>${buildSymbolizer(
          effective,
          geometry,
          effective.color,
        )}</Rule>`;
      })
      .join('');

    rules += `<Rule><Name>other</Name><Title>All other values</Title><ElseFilter/>${buildSymbolizer(
      renderer,
      geometry,
      renderer.defaultColor ?? '#94A3B8',
    )}</Rule>`;
  } else {
    rules = `<Rule>${buildSymbolizer(renderer, geometry, renderer.color)}</Rule>`;
  }

  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<StyledLayerDescriptor version="1.0.0"` +
    ` xmlns="http://www.opengis.net/sld"` +
    ` xmlns:ogc="http://www.opengis.net/ogc"` +
    ` xmlns:xlink="http://www.w3.org/1999/xlink">` +
    `<NamedLayer><Name>${escapeXml(layerName)}</Name>` +
    `<UserStyle><FeatureTypeStyle>${rules}</FeatureTypeStyle></UserStyle>` +
    `</NamedLayer></StyledLayerDescriptor>`
  );
}

/** Guard against SLD documents too long to survive a GET tile request. */
export const MAX_SLD_URL_LENGTH = 7000;

export function isSldTooLarge(sld: string): boolean {
  return encodeURIComponent(sld).length > MAX_SLD_URL_LENGTH;
}

/**
 * Detects a WFS layer's geometry type via DescribeFeatureType.
 * WMS GetCapabilities does not publish geometry type, so the matching WFS
 * endpoint is the only reliable source.
 */
export async function detectWfsGeometryType(
  geoServerUrl: string,
  layerName: string,
): Promise<LayerGeometryType> {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '1.1.0');
    url.searchParams.set('request', 'DescribeFeatureType');
    url.searchParams.set('typeName', layerName);

    const response = await fetch(url.toString());
    if (!response.ok) {
      console.error(
        `[sld] DescribeFeatureType failed (${response.status}) layer="${layerName}"\n  ${url}`,
      );
      return 'unknown';
    }

    const text = await response.text();

    // GeoServer reports many failures as HTTP 200 with an OWS exception body
    // (unknown typeName, WFS disabled, workspace mismatch). Without this the
    // schema simply "has no geometry" and the layer silently degrades to
    // colour-only styling with nothing in the console to explain why.
    if (/ExceptionReport|ServiceException/i.test(text)) {
      const reason = text.match(/<ows:ExceptionText>([\s\S]*?)<\/ows:ExceptionText>/i)?.[1];
      console.warn(
        `[sld] DescribeFeatureType returned an exception for "${layerName}": ${
          reason?.trim() ?? 'unknown reason'
        } — falling back to a feature probe.`,
      );
    } else {
      // e.g. type="gml:MultiPolygonPropertyType"
      const match = text.match(/type="gml:(\w+)PropertyType"/);
      const gmlType = (match?.[1] ?? '').toLowerCase();

      if (gmlType.includes('point')) return 'point';
      if (gmlType.includes('line') || gmlType.includes('curve')) return 'line';
      if (gmlType.includes('polygon') || gmlType.includes('surface')) return 'polygon';
    }

    // Schema was inconclusive. Common causes: a generic
    // `gml:GeometryPropertyType` column (mixed-geometry tables), an
    // unrecognised namespace prefix, or the exception above. Reading one real
    // feature settles it, because the data itself carries the geometry type.
    return await probeGeometryFromFeature(geoServerUrl, layerName);
  } catch (error) {
    console.error(`[sld] geometry detection failed layer="${layerName}"`, error);
    return 'unknown';
  }
}

/** Maps a GeoJSON geometry type onto our normalised family. */
function normaliseGeoJsonGeometry(type: string | undefined): LayerGeometryType {
  switch ((type ?? '').toLowerCase()) {
    case 'point':
    case 'multipoint':
      return 'point';
    case 'linestring':
    case 'multilinestring':
      return 'line';
    case 'polygon':
    case 'multipolygon':
      return 'polygon';
    default:
      return 'unknown';
  }
}

/**
 * Last-resort geometry detection: fetch a single feature and look at it.
 *
 * Costs one small request and only runs when the schema could not answer, but
 * it is authoritative — the geometry in the response is the geometry the layer
 * actually has.
 */
async function probeGeometryFromFeature(
  geoServerUrl: string,
  layerName: string,
): Promise<LayerGeometryType> {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
    url.search = '';
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '2.0.0');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('typeNames', layerName);
    url.searchParams.set('outputFormat', 'application/json');
    url.searchParams.set('count', '1');

    const response = await fetch(url.toString());
    if (!response.ok) return 'unknown';

    const data = await response.json();
    // A feature with null geometry tells us nothing; scan the returned page.
    for (const feature of data?.features ?? []) {
      const resolved = normaliseGeoJsonGeometry(feature?.geometry?.type);
      if (resolved !== 'unknown') return resolved;
    }
    return 'unknown';
  } catch (error) {
    console.warn(`[sld] feature probe failed layer="${layerName}"`, error);
    return 'unknown';
  }
}

/**
 * Distinct values for a field, read over WFS.
 * GeoServer has no `returnDistinctValues`, so values are de-duplicated here.
 */
export async function fetchWfsDistinctValues(
  geoServerUrl: string,
  layerName: string,
  fieldName: string,
  limit = 20,
): Promise<string[]> {
  const url = new URL(geoServerUrl.trim());
  const path = url.pathname.replace(/\/+$/, '');
  url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
  url.search = '';
  url.searchParams.set('service', 'WFS');
  url.searchParams.set('version', '2.0.0');
  url.searchParams.set('request', 'GetFeature');
  url.searchParams.set('typeNames', layerName);
  url.searchParams.set('propertyName', fieldName);
  url.searchParams.set('outputFormat', 'application/json');
  url.searchParams.set('count', '1000');

  const response = await fetch(url.toString());
  if (!response.ok) throw new Error(`WFS returned ${response.status}`);
  const data = await response.json();

  // De-duplicate on the TRIMMED value: source data frequently contains the same
  // logical value with stray whitespace ("63 mm" vs "63 mm "), which would
  // otherwise produce two classes that render identically in HTML.
  const values = new Set<string>();
  for (const feature of data?.features ?? []) {
    const value = feature?.properties?.[fieldName];
    if (value === null || value === undefined) continue;
    const normalised = String(value).trim();
    if (normalised !== '') values.add(normalised);
    if (values.size >= limit) break;
  }
  return Array.from(values);
}

/** Field names available on a WFS layer, for the unique-value renderer. */
export async function fetchWfsFields(
  geoServerUrl: string,
  layerName: string,
): Promise<{ name: string; alias: string }[]> {
  try {
    const fields = await fetchWfsFieldTypes(geoServerUrl, layerName);
    return fields.map((field) => ({ name: field.name, alias: field.name }));
  } catch (error) {
    console.error(`[sld] field list failed layer="${layerName}"`, error);
    return [];
  }
}

/**
 * Field names AND their DescribeFeatureType `type` attribute.
 *
 * `fetchWfsFields` above throws the type away (its callers only ever wanted
 * names). The Time Slider tool needs it too, to tell a date/dateTime column
 * apart from everything else — split out rather than changing
 * `fetchWfsFields`'s return shape, so every existing caller is untouched.
 */
export async function fetchWfsFieldTypes(
  geoServerUrl: string,
  layerName: string,
): Promise<{ name: string; type: string }[]> {
  const url = new URL(geoServerUrl.trim());
  const path = url.pathname.replace(/\/+$/, '');
  url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, '/wfs') : `${path}/wfs`;
  url.search = '';
  url.searchParams.set('service', 'WFS');
  url.searchParams.set('version', '1.1.0');
  url.searchParams.set('request', 'DescribeFeatureType');
  url.searchParams.set('typeName', layerName);

  const response = await fetch(url.toString());
  if (!response.ok) return [];

  const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
  return Array.from(xml.getElementsByTagName('xsd:element'))
    .concat(Array.from(xml.getElementsByTagName('element')))
    .map((node) => ({
      name: node.getAttribute('name') ?? '',
      type: node.getAttribute('type') ?? '',
    }))
    .filter((field) => field.name && !/^gml:/i.test(field.type));
}
